// src/scenes/sitemanager/DayEnd.jsx
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    useTheme,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    FormControl,
    Select,
    MenuItem
} from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import axios from "axios";
import { tokens } from "../../theme";
import { useAuth } from "../../context/AuthContext";

import MGRDayEndIncomeEntry from "../../component-files/Forms/MGRIncomeEntry";
import MGRDayEndMealCountsEntry from "../../component-files/Forms/MGRDayEndMealCountEntry";

export default function DayEnd() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { allocations } = useAuth();
    const allowedIds = allocations.map(a => a.SchoolId);

    const [schools, setSchools] = useState([]);
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [loading, setLoading] = useState(true);

    // Labor table state
    const blankLabor = () => ({ tempId: Date.now(), SchoolId: "", Date: "", LaborHours: "", Type: "Monthly" });
    const [laborRows, setLaborRows] = useState([]);
    const [laborSaving, setLaborSaving] = useState(false);
    const [laborError, setLaborError] = useState("");

    useEffect(() => {
        async function loadSchools() {
            try {
                const res = await axios.get("https://figazuresql.swancreate.com/api/swa/SchoolMst");
                const data = res.data;
                const filtered = data
                    .filter(s => allowedIds.includes(s.SchoolId))
                    .sort((a, b) => a.SchoolName.localeCompare(b.SchoolName));
                setSchools(filtered);
                setLaborRows([{ ...blankLabor(), SchoolId: filtered[0]?.SchoolId || "" }]);
            } catch (e) {
                console.error("Failed to load schools", e);
            } finally {
                setLoading(false);
            }
        }
        loadSchools();
    }, [allocations]);

    const handleRefresh = () => setRefreshFlag(f => !f);

    const handleLaborChange = (idx, field, value) => {
        const updated = [...laborRows];
        updated[idx][field] = value;
        setLaborRows(updated);
    };
    const addLaborRow = () => setLaborRows(r => [...r, { ...blankLabor(), SchoolId: schools[0]?.SchoolId || "" }]);
    const removeLaborRow = idx => setLaborRows(r => r.filter((_, i) => i !== idx));

    const submitLabor = async () => {
        setLaborError("");
        for (const r of laborRows) {
            if (!r.SchoolId || !r.Date || !r.LaborHours) {
                setLaborError("All fields required in each labor row.");
                return;
            }
        }
        setLaborSaving(true);
        try {
            for (const r of laborRows) {
                const school = schools.find(s => s.SchoolId === r.SchoolId);
                const payload = {
                    SchoolId: r.SchoolId,
                    SchoolName: school?.SchoolName || "",
                    Date: r.Date,
                    Month: new Date(r.Date).toLocaleString("default", { month: "long" }),
                    Year: new Date(r.Date).getFullYear().toString(),
                    LaborHours: parseFloat(r.LaborHours) || 0,
                    Type: r.Type,
                    isActive: 1
                };
                await axios.post("https://figazuresql.swancreate.com/api/swa/FINANCE_LaborHours", payload);
            }
            handleRefresh();
            setLaborRows([{ ...blankLabor(), SchoolId: schools[0]?.SchoolId || "" }]);
        } catch (e) {
            console.error("Labor save failed", e);
            setLaborError("Save failed, check console.");
        } finally {
            setLaborSaving(false);
        }
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
    }

    return (
        <Box p={3} sx={{ backgroundColor: colors.background, color: colors.text, minHeight: '100vh' }}>
            <Typography
                variant="h4"
                mb={2}
                sx={{ fontWeight: 600, textShadow: '0 0 8px #00FFFF' }}
            >
                Day-End Submission Dashboard
            </Typography>
            <Typography
                variant="subtitle1"
                mb={3}
                sx={{ textShadow: '0 0 4px #00FFFF' }}
            >
                Complete each section below for all your sites.
            </Typography>

            {/* Income Section */}
            <Box mb={5} sx={{ p: 2, backgroundColor: theme.palette.background.paper, borderRadius: 2, boxShadow: '0 0 12px 2px #00FFFF' }}>
                <Typography variant="h5" mb={1}>1. Record Day-End Incomes</Typography>
                <Typography variant="body2" mb={2}>
                    Please enter any income for the day (e.g., catering, adult sales, a la carte, etc.).
                </Typography>
                <MGRDayEndIncomeEntry key={`income-${refreshFlag}`} schoolId={null} date={null} onSaved={handleRefresh} />
            </Box>

            {/* Meal Counts Section */}
            <Box mb={5} sx={{ p: 2, backgroundColor: theme.palette.background.paper, borderRadius: 2, boxShadow: '0 0 12px 2px #00FF00' }}>
                <Typography variant="h5" mb={1}>2. Record Meal Counts</Typography>
                <Typography variant="body2" mb={2}>
                    Please record total meal counts for each site and date, including Free, Reduced, and Paid.
                </Typography>
                <MGRDayEndMealCountsEntry key={`meals-${refreshFlag}`} schoolId={null} date={null} onSaved={handleRefresh} />
            </Box>

            {/* Labor Hours Table */}
            <Box mb={5} sx={{ p: 2, backgroundColor: theme.palette.background.paper, borderRadius: 2, boxShadow: '0 0 12px 2px #FFFF00' }}>
                <Typography variant="h5" mb={1}>3. Submit Labor Hours</Typography>
                <Typography variant="body2" mb={2}>
                    Please log staff hours worked today for each site and date.
                </Typography>
                <Table size="small" sx={{ backgroundColor: colors.backgroundLight }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Site</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Hours</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {laborRows.map((r, idx) => (
                            <TableRow key={r.tempId}>
                                <TableCell>
                                    <FormControl size="small" fullWidth>
                                        <Select value={r.SchoolId} onChange={e => handleLaborChange(idx, 'SchoolId', e.target.value)}>
                                            {schools.map(s => <MenuItem key={s.SchoolId} value={s.SchoolId}>{s.SchoolName}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <TextField type="date" size="small" value={r.Date} onChange={e => handleLaborChange(idx, 'Date', e.target.value)} InputLabelProps={{ shrink: true }} />
                                </TableCell>
                                <TableCell>
                                    <TextField type="number" size="small" value={r.LaborHours} onChange={e => handleLaborChange(idx, 'LaborHours', e.target.value)} inputProps={{ step: 0.01 }} />
                                </TableCell>
                                <TableCell>
                                    <FormControl size="small" fullWidth>
                                        <Select value={r.Type} onChange={e => handleLaborChange(idx, 'Type', e.target.value)}>
                                            <MenuItem value="Daily">Daily</MenuItem>
                                            <MenuItem value="Weekly">Weekly</MenuItem>
                                            <MenuItem value="Monthly">Monthly</MenuItem>
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <IconButton onClick={() => removeLaborRow(idx)}><RemoveCircleOutline /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell colSpan={5} align="center">
                                <IconButton onClick={addLaborRow}><AddCircleOutline /> Add Row</IconButton>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                {laborError && <Typography color="error" mt={1}>{laborError}</Typography>}
                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button variant="contained" onClick={submitLabor} disabled={laborSaving}>{laborSaving ? <CircularProgress size={20} /> : 'Save All Labor'}</Button>
                </Box>
            </Box>

            <Box textAlign="center" mt={4}>
                <Button variant="contained" size="large" sx={{ backgroundColor: theme.palette.primary.main }} onClick={() => alert('All sections submitted!')}>Complete Day End</Button>
            </Box>
        </Box>
    );
}
